import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole } from './useUserRole';
import { User } from '@supabase/supabase-js';

interface NavItem {
  to: string;
  label: string;
}

export const usePersonalizedNav = (user: User | null) => {
  const [navItems, setNavItems] = useState<NavItem[]>([]);
  const { isPremium } = useUserRole(user);

  useEffect(() => {
    const loadPersonalizedNav = async () => {
      if (!user) {
        setNavItems([]);
        return;
      }

      // Base items always shown
      const baseItems: NavItem[] = [
        { to: '/therapy', label: 'Therappi®' },
        { to: '/daily-check-in', label: 'Check-In' }
      ];

      // Get user preferences
      const { data: profile } = await supabase
        .from('profiles')
        .select('track_womens_wellness, track_spiritual, track_manifestation, track_nutrition')
        .eq('id', user.id)
        .single();

      // Get user's page visit history from audit logs
      const { data: logs } = await supabase
        .from('audit_logs')
        .select('details')
        .eq('user_id', user.id)
        .eq('action', 'page_visit')
        .gte('created_at', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
        .limit(200);

      // Count page visits
      const pageCounts: Record<string, number> = {};
      logs?.forEach(log => {
        const page = (log.details as any)?.page;
        if (page && typeof page === 'string') {
          pageCounts[page] = (pageCounts[page] || 0) + 1;
        }
      });

      // Map pages to nav items with preference filtering
      const pageToNavItem: Record<string, NavItem & { requiresTrack?: keyof typeof profile }> = {
        '/sanctuary': { to: '/sanctuary', label: 'The Sanctuary' },
        '/meditations': { to: '/meditations', label: 'Meditations' },
        '/manifestation': { to: '/manifestation', label: 'Manifestation', requiresTrack: 'track_manifestation' },
        '/telempathy-tools': { to: '/telempathy-tools', label: 'Telempathy', requiresTrack: 'track_spiritual' },
        '/sleep-and-dreams': { to: '/sleep-and-dreams', label: 'Sleep & Dreams' },
        '/future-self-navigator': { to: '/future-self-navigator', label: 'Future Self', requiresTrack: 'track_manifestation' },
        '/cycle-and-lunar': { to: '/cycle-and-lunar', label: 'Cycle & Lunar', requiresTrack: 'track_womens_wellness' },
        '/food-mood': { to: '/food-mood', label: 'Food-Mood', requiresTrack: 'track_nutrition' },
        '/inflammation': { to: '/inflammation', label: 'Inflammation' },
        '/longevity': { to: '/longevity', label: 'Longevity' },
        '/nutrition': { to: '/nutrition', label: 'Nutrition', requiresTrack: 'track_nutrition' }
      };

      // Filter available pages based on user preferences
      const availablePages = Object.entries(pageToNavItem).filter(([, item]) => {
        if (!item.requiresTrack) return true;
        return profile?.[item.requiresTrack] === true;
      });

      // Get top 2 most visited pages from available pages (excluding base pages)
      const topPages = Object.entries(pageCounts)
        .filter(([page]) => {
          // Must not be a base page
          if (baseItems.some(item => item.to === page)) return false;
          // Must be in available pages
          return availablePages.some(([path]) => path === page);
        })
        .sort(([, a], [, b]) => b - a)
        .slice(0, 2)
        .map(([page]) => {
          const item = pageToNavItem[page];
          return item ? { to: item.to, label: item.label } : null;
        })
        .filter((item): item is NavItem => item !== null);

      // Combine base items with top pages
      const personalizedItems = [...baseItems, ...topPages];

      setNavItems(personalizedItems);
    };

    loadPersonalizedNav();
  }, [user, isPremium]);

  return navItems;
};
